import { useState, useEffect, useCallback } from 'react';
import { directSupabase } from '../lib/supabaseClient';
import type { Database } from '../types/supabase';

export type SupplierPrice = Database['public']['Tables']['supplier_prices']['Row'];
export type GlassExtra = Database['public']['Tables']['glass_extras']['Row'];
export type SupplierExtra = Database['public']['Tables']['supplier_extras']['Row'] & {
  glass_extras?: Pick<GlassExtra, 'name'>;
};

export interface UnifiedExtra {
  extra_id: string;
  name: string;
  price: number | null;
  isFallback: boolean;
}

export interface UnifiedPricing {
  prices: SupplierPrice[];
  extras: UnifiedExtra[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

// Helper to extract makeup from glass_type string (e.g., 'Double - 4-16-4 - Float (Clear)')
export function extractMakeupFromGlassType(glass_type: string): string {
  const match = glass_type.match(/Double\s*-\s*([^-]+)-/);
  if (match) {
    return match[1].trim();
  }
  return '';
}

export function useUnifiedPricing(supplierId: string | null): UnifiedPricing {
  const [prices, setPrices] = useState<UnifiedPricing['prices']>([]);
  const [extras, setExtras] = useState<UnifiedPricing['extras']>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    console.log('useUnifiedPricing: supplierId', supplierId);
    if (!supplierId) {
      setPrices([]);
      setExtras([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Fetch all data in parallel
      const [supplierPricesRes, glassExtrasRes, supplierExtrasRes] = await Promise.all([
        directSupabase.from('supplier_prices').select('*').eq('supplier_id', supplierId),
        directSupabase.from('glass_extras').select('*'),
        directSupabase.from('supplier_extras').select('*, glass_extras(name)').eq('supplier_id', supplierId),
      ]);
      console.log('supplierPricesRes', supplierPricesRes);

      // Merge prices with fallback
      const supplierPricesList: SupplierPrice[] = supplierPricesRes.data || [];
      const glassExtrasList: GlassExtra[] = glassExtrasRes.data || [];
      // Map supplier extras by extra_id
      const supplierExtraMap: Record<string, SupplierExtra> = Object.fromEntries(
        (supplierExtrasRes.data?.map((e: SupplierExtra) => [e.extra_id, e]) || [])
      );
      // Build unified extras array
      const mergedExtras: UnifiedExtra[] = glassExtrasList.map((glassExtra: GlassExtra) => {
        const supplierExtra = supplierExtraMap[glassExtra.id];
        if (supplierExtra) {
          return {
            extra_id: glassExtra.id,
            name: supplierExtra.glass_extras?.name || glassExtra.name,
            price: supplierExtra.price,
            isFallback: false,
          };
        } else {
          return {
            extra_id: glassExtra.id,
            name: glassExtra.name,
            price: glassExtra.price ?? null,
            isFallback: true,
          };
        }
      });

      setPrices(supplierPricesList);
      setExtras(mergedExtras);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing');
    } finally {
      setIsLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    prices,
    extras,
    isLoading,
    error,
    refresh: fetchData,
  };
} 